import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faUsers,
  faCircleUser,
  faComputerClassic,
  faTag,
  faBullhorn,
  faUsersRectangle,
  faEnvelope,
  faAddressCard,
  faDiagramProject,
} from "@fortawesome/pro-regular-svg-icons";
import {
  ChannelEntry,
  GroupAdminEntry,
  GroupCategoryEntry,
  GroupEntry,
  MemberEntry,
} from "../../../components/directory/api";
import { isEmpty, get } from "lodash";
import {
  ContactEntry,
  DisabledOption,
  DistributionEntry,
  ExternalInviteEntry,
  MembershipRole,
  PermissionPickable,
  PermissionPickableWithDisabled,
  SystemRole,
  ValueType,
} from "../../../components/shared/DirectoryPicker/types";
import { useQuery } from "react-query";
import classnames from "classnames";
import { profileFieldAvailable } from "../../memberships/MemberNameWithAdditions";
import "./optionRendering.css";

// Externally-visible signature, for compile time check that switch uses all types
export function throwBadType(entry: never): never;
// Implementation signature, called if invalid type is provided during runtime
export function throwBadType(entry: PermissionPickableWithDisabled) {
  throw new Error(
    "Unhandled option type: " + entry.type + " in " + JSON.stringify(entry),
  );
}

/**
 * Turns a given option value into a react element
 * If display attributes (like name) are missing, they are fetched by the react components
 *
 * @param entry
 * @param query
 */
export function formatOptionLabel(
  entry: ValueType | PermissionPickableWithDisabled,
  query?: { [key: string]: boolean | string | number },
) {
  if (!isOptionEnabled(entry)) return noOptionsMessage({ inputValue: "" });

  switch (entry.type) {
    case "member":
      return <MemberOptionLabel {...entry} />;

    case "group_category":
      return <GroupCategoryOptionLabel {...entry} />;

    case "group":
      return (
        <GroupOptionLabel
          {...entry}
          showOnlyGroupName={!!get(query, "groups_only")}
        />
      );

    case "group_admin":
      return <GroupAdminOptionLabel {...entry} />;

    case "system_role":
      return <SystemRoleOptionLabel {...entry} />;

    case "membership_role":
      return <MembershipRoleOptionLabel {...entry} />;

    case "channel":
      return <ChannelOptionLabel {...entry} />;

    case "external_invite":
      return <ExternalInviteOptionLabel {...entry} />;

    case "contact":
      return <ContactOptionLabel {...entry} />;

    case "distribution":
      return <DistributionOptionLabel {...entry} />;

    default:
      throwBadType(entry);
  }
}

function MemberOptionLabel({
  id,
  name,
  images,
  detection_profile_field,
}: MemberEntry) {
  const { data, isLoading } = useQuery<MemberEntry>(`/members/${id}`, {
    enabled: !name,
  });

  const displayName = isLoading
    ? I18n.t("js.loading")
    : name || data?.name || I18n.t("js.memberships.deleted_name");
  const profileField = detection_profile_field || data?.detection_profile_field;

  return (
    <div className="flex gap-2 items-center">
      {images ? (
        <img className="member-image h-6 w-6" src={images.small} />
      ) : (
        <FontAwesomeIcon key="icon" icon={faUser} className="fa-fw" />
      )}
      {profileFieldAvailable({ detectionProfileField: profileField })
        ? `${displayName} (${profileField})`
        : displayName}
    </div>
  );
}

function ChannelOptionLabel({ slug, title, image }: ChannelEntry) {
  const { data, isLoading } = useQuery<ChannelEntry>(`/channels/${slug}`, {
    enabled: !slug,
  });

  return (
    <div className="flex gap-1 items-center">
      {image ? (
        <img
          className="member-image h-6 w-6"
          src={`/api/storage/images/${image.image_id}/get`}
        />
      ) : (
        <FontAwesomeIcon key="icon" icon={faBullhorn} className="fa-fw" />
      )}
      {isLoading ? I18n.t("js.loading") : title || data?.title}
    </div>
  );
}

function GroupCategoryOptionLabel({ id, plural_name }: GroupCategoryEntry) {
  const data = Preload.current_network.group_categories.find(
    (gc) => gc.id == id,
  );

  return (
    <>
      <FontAwesomeIcon
        key="icon"
        icon={faUsersRectangle}
        className="fa-fw mr-2"
      />
      {`${
        plural_name || data?.plural_name || I18n.t("js.groups.unknown_group")
      }`}
    </>
  );
}

function GroupOptionLabel({
  id,
  name,
  showOnlyGroupName,
}: Pick<GroupEntry, "id" | "name"> & { showOnlyGroupName?: boolean }) {
  const { data, isLoading } = useQuery<GroupEntry>(`/groups/${id}`, {
    enabled: !name,
  });

  return (
    <>
      <FontAwesomeIcon key="icon" icon={faUsers} className="fa-fw mr-2" />
      {isLoading
        ? I18n.t("js.loading")
        : `${name || data?.name || I18n.t("js.groups.unknown_group")} ${
            !showOnlyGroupName
              ? "(" + I18n.t("js.member_select.all_group_members") + ")"
              : ""
          }`}
    </>
  );
}

function GroupAdminOptionLabel({
  id,
  name,
}: Pick<GroupAdminEntry, "id" | "name">) {
  const { data, isLoading } = useQuery<GroupAdminEntry>(`/groups/${id}`, {
    enabled: !name,
  });

  return (
    <>
      <FontAwesomeIcon
        key="icon"
        icon={faCircleUser}
        className="fa-fw mr-2 group_indentation"
      />
      {isLoading
        ? I18n.t("js.loading")
        : `${name || data?.name || I18n.t("js.groups.unknown_group")} (${I18n.t(
            "js.member_select.only_admins",
          )})`}
    </>
  );
}

function SystemRoleOptionLabel({ id, name }: SystemRole) {
  return (
    <>
      <FontAwesomeIcon
        key="icon"
        icon={faComputerClassic}
        className="fa-fw mr-2"
      />
      {name || I18n.t(`js.permission_picker.system_roles.${id}`)}
    </>
  );
}

function MembershipRoleOptionLabel({
  id,
  name,
  parent_type,
  parent_id,
  standalone,
}: MembershipRole) {
  const { data: groupData, isLoading } = useQuery<GroupEntry>(
    `/groups/${parent_id}`,
    {
      enabled: parent_type == "group" && !name,
    },
  );
  let data: Pick<MembershipRole, "name"> | undefined;
  if (parent_type == "group")
    data = groupData?.membership_roles?.find((r) => r.id == id);
  else data = Preload.current_network.membership_roles?.find((r) => r.id == id);

  // If no role found yet, search in all my_groups (used for group role filter)
  if (!data) {
    data = Preload.my_groups
      ?.flatMap((group) => group.membership_roles)
      .find((role) => role.id == id);
  }

  return (
    <>
      <FontAwesomeIcon
        key="icon"
        icon={faTag}
        className={classnames("fa-fw mr-2", {
          group_indentation: parent_type == "group" && !standalone,
        })}
      />
      {isLoading
        ? I18n.t("js.loading")
        : name || data?.name || I18n.t("js.permission_picker.unknown_role")}
    </>
  );
}

function ContactOptionLabel({ name }: ContactEntry) {
  return (
    <div className="flex gap-2 items-center">
      <FontAwesomeIcon icon={faAddressCard} className="fa-fw" />
      {name}
    </div>
  );
}

function DistributionOptionLabel({ name }: DistributionEntry) {
  return (
    <div className="flex gap-2 items-center">
      <FontAwesomeIcon icon={faDiagramProject} className="fa-fw" />
      {name}
    </div>
  );
}

function ExternalInviteOptionLabel({ name, subtitle }: ExternalInviteEntry) {
  return (
    <div className="flex gap-2 items-center">
      <FontAwesomeIcon icon={faEnvelope} className="fa-fw" />
      <div>
        <div>{name}</div>
        {subtitle && <div className="text-muted text-sm">{subtitle}</div>}
      </div>
    </div>
  );
}

function isOptionEnabled(entry: ValueType): entry is PermissionPickable {
  return !("disabled" in entry);
}

export function isOptionDisabled(entry: ValueType): entry is DisabledOption {
  return !isOptionEnabled(entry);
}

export const noOptionsMessage = ({ inputValue }) =>
  isEmpty(inputValue)
    ? I18n.t("js.plugins.select2.enter_more_characters.one")
    : I18n.t("js.plugins.select2.no_match");
